import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { getOrderDriver } from "../../api/axios/requests";
import { DriverStageBadge } from "../../components/orderProgress";

// ─────────────────────────────────────────────────────────────────────────────
// ЖОЛООЧИЙН САМБАР (2026-09-17) — захиалгын дэлгэрэнгүй цонхонд.
//
//  • «Жолооч ирж байна» / «Жолооч ирсэн», нэр, утас, машин, ойролцоо хугацаа.
//  • Газрын зураг (OpenStreetMap — админ вэбтэй ижил, түлхүүргүй, үнэгүй).
//    Сервер жолоочийн байршлыг ЗӨВХӨН ЭНЭ ресторан руу явж байгаа эсвэл ирсэн
//    үед өгнө — жолооч өөр захиалга хүргэж яваа замыг мерчант харахгүй.
//  • 10 сек тутам шинэчилнэ, цонх хаагдмагц зогсоно.
//  • ⚠️ Leaflet-ийн анхдагч тэмдэг webpack дээр зураггүй гардаг тул дугуй тэмдэг.
// ─────────────────────────────────────────────────────────────────────────────

const POLL_MS = 10 * 1000;
const ERDENET = [49.0279, 104.0481];

function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => p.join(",")).join("|");
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) map.setView(points[0], 16);
    else map.fitBounds(points, { padding: [30, 30], maxZoom: 17 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return null;
}

export default function DriverPanel({ orderId }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    let timer = null;
    const load = async () => {
      try {
        const res = await getOrderDriver(orderId);
        if (!alive.current) return;
        if (res?.success) {
          setInfo(res.data || null);
          setError(false);
        }
      } catch {
        if (alive.current) setError(true);
      } finally {
        if (alive.current) timer = setTimeout(load, POLL_MS);
      }
    };
    load();
    return () => {
      alive.current = false;
      if (timer) clearTimeout(timer);
    };
  }, [orderId]);

  const points = useMemo(() => {
    const out = [];
    if (info?.merchant) out.push([info.merchant.lat, info.merchant.lng]);
    if (info?.location) out.push([info.location.latitude, info.location.longitude]);
    return out;
  }, [info]);

  if (!info || !info.stage) return null;

  const d = info.driver;
  const name = [d?.lastName ? `${d.lastName.charAt(0)}.` : "", d?.firstName || ""].join("").trim();
  const showMap = info.stage !== "ASSIGNED" && points.length > 0;

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="fw-bold">Жолооч</span>
          {info.stage === "ASSIGNED" ? (
            <span className="badge bg-secondary">Хүлээж авсан</span>
          ) : (
            <DriverStageBadge stage={info.stage} />
          )}
        </div>

        <p className="mb-1">
          {name || "—"}
          {d?.phone ? (
            <a className="ms-2" href={`tel:${d.phone}`} onClick={(e) => e.stopPropagation()}>
              {d.phone}
            </a>
          ) : null}
        </p>
        {d?.vehicle || d?.plate ? (
          <p className="mb-1 text-muted">{[d.vehicle, d.plate].filter(Boolean).join(" · ")}</p>
        ) : null}
        {info.stage === "HEADING" && info.etaMin != null ? (
          <p className="mb-2">
            Ойролцоогоор <span className="fw-bold">{info.etaMin} мин</span>
            {info.distanceM != null ? (
              <span className="text-muted">
                {" "}
                · {info.distanceM < 1000 ? `${info.distanceM} м` : `${(info.distanceM / 1000).toFixed(1)} км`}
              </span>
            ) : null}
          </p>
        ) : null}
        {error ? <p className="small text-danger mb-2">Байршил шинэчлэгдэхгүй байна…</p> : null}

        {showMap ? (
          <div style={{ height: 260, borderRadius: 8, overflow: "hidden" }}>
            <MapContainer
              center={points[0] || ERDENET}
              zoom={15}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {info.merchant ? (
                <CircleMarker
                  center={[info.merchant.lat, info.merchant.lng]}
                  radius={9}
                  pathOptions={{ color: "#fff", weight: 2, fillColor: "#FF3008", fillOpacity: 1 }}
                >
                  <Tooltip>Таны байгууллага</Tooltip>
                </CircleMarker>
              ) : null}
              {info.location ? (
                <CircleMarker
                  center={[info.location.latitude, info.location.longitude]}
                  radius={9}
                  pathOptions={{ color: "#fff", weight: 2, fillColor: "#1A73E8", fillOpacity: 1 }}
                >
                  <Tooltip>Жолооч</Tooltip>
                </CircleMarker>
              ) : null}
              <FitBounds points={points} />
            </MapContainer>
          </div>
        ) : null}
      </div>
    </div>
  );
}
